import React, { useState } from 'react'
import TextButton from '../../Buttons/TextButton'
import useLimitedMode from '../../../services/subscription/useLimitedMode'
import Spinner from '../../UI/Spinner'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from '../../../services/api'
import {
  getApiErrorMessage,
  hasApiFieldError,
} from '../../../services/apiError'
import { toast } from 'react-toastify'
import { useAuthContext } from '../../../context/auth/auth'
import InputWrapper from '../../Formik/InputWrapper'
import Label from '../../Formik/Label'
import Input from '../../Formik/Input'
import FormikErrorText from '../../Formik/FormikErrorText'

const Security = () => {
  const { t } = useTranslation()
  const { isLimited } = useLimitedMode()
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const { authClient, token } = useAuthContext()
  const validationSchema = yup.object({
    currentPassword: yup.string().required(t('requiredField')),
    password: yup
      .string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&.*])(?=.{8,})/,
        t(
          'passwordMustBeAtLeast8CharactersAndContainAtLeastOneUppercaseLetterAndOneNumber',
        ),
      )
      .required(t('requiredField')),
    passwordConfirmation: yup
      .string()
      .oneOf([yup.ref('password'), null], t('passwordsMustMatch'))
      .required(t('requiredField')),
  })

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      password: '',
      passwordConfirmation: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      try {
        if (!authClient?.id) {
          toast.error(t('failedToChangePassword'))
          return
        }

        // `current_password` is required and checked server-side by
        // `partial_update_user`. This form used to re-authenticate against
        // `POST /login/` instead, because the PATCH copied every key straight
        // onto the model and discarded the field - so it verified nothing.
        // The server check is real now, and duplicating it would only cost a
        // round trip.
        const response = await axios.patch(
          `/auth/user/${authClient.id}/`,
          {
            password: values.password,
            current_password: values.currentPassword,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (response.status === 200) {
          toast.success(t('yourPasswordHasBeenChanged'))
          setIsEdit(false)
          resetForm()
        }
      } catch (error) {
        // A rejected current password belongs against the field that holds it,
        // not in a toast the user has to map back onto the form.
        const message = getApiErrorMessage(error)
        // The backend raises `ValidationError('Current password is
        // incorrect!')`, a bare list with no field attached, so the text is
        // the only handle today. `hasApiFieldError` takes over on its own if
        // that ever becomes a field-keyed error.
        const isCurrentPasswordRejected =
          hasApiFieldError(error, 'current_password') ||
          (!!message && /current.?password/i.test(message))
        if (isCurrentPasswordRejected) {
          setFieldError('currentPassword', t('currentPasswordIsIncorrect'))
          return
        }
        toast.error(message ?? t('failedToChangePassword'))
      } finally {
        setSubmitting(false)
      }
    },
  })
  const isSubmitting = formik.isSubmitting
  const handleEdit = () => {
    if (isEdit) {
      formik.resetForm()
    }
    setIsEdit((prev) => !prev)
  }
  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='flex items-center justify-between flex-wrap gap-3'>
        <h2 className='font-bold text-2xl text-[#3D3D3D]'>{t('security')}</h2>
        <div className='flex items-center gap-5'>
          {!isEdit && (
            <TextButton
              disabled={isSubmitting || isLimited}
              title={isLimited ? t('disabledUntilActivation') : undefined}
              label={t('changePassword')}
              onClickHandler={handleEdit}
            />
          )}
          {isEdit && (
            <>
              <TextButton
                variant='secondary'
                label={t('cancel')}
                disabled={isSubmitting}
                onClickHandler={handleEdit}
              />
              {isSubmitting ? (
                <Spinner spinner='form' color='#0C75FF' />
              ) : (
                <TextButton
                  disabled={isSubmitting}
                  label={t('update')}
                  type='submit'
                />
              )}
            </>
          )}
        </div>
      </div>
      <div
        className={`grid gap-5 sm:gap-10 mt-8 ${
          isEdit ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
        }`}
      >
        {isEdit ? (
          <>
            <InputWrapper width='w-full'>
              <Label text={t('currentPassword')} />
              <Input
                id='Current Password'
                as='input'
                type='password'
                placeholder={t('insertPassword')}
                hasError={
                  !!(
                    formik.touched.currentPassword &&
                    formik.errors.currentPassword
                  )
                }
                {...formik.getFieldProps('currentPassword')}
              />
              {formik.errors.currentPassword && (
                <FormikErrorText errorText={formik.errors.currentPassword} />
              )}
            </InputWrapper>
            <InputWrapper width='w-full'>
              <Label text={t('newPassword')} />
              <Input
                id='Password'
                as='input'
                type='password'
                placeholder={t('insertPassword')}
                hasError={!!(formik.touched.password && formik.errors.password)}
                {...formik.getFieldProps('password')}
              />
              {formik.errors.password && (
                <FormikErrorText errorText={formik.errors.password} />
              )}
            </InputWrapper>
            <InputWrapper width='w-full'>
              <Label text={t('reEnterNewPassword')} />
              <Input
                id='Confirm Password'
                as='input'
                type='password'
                placeholder={t('insertPassword')}
                hasError={
                  !!(
                    formik.touched.passwordConfirmation &&
                    formik.errors.passwordConfirmation
                  )
                }
                {...formik.getFieldProps('passwordConfirmation')}
              />
              {formik.errors.passwordConfirmation && (
                <FormikErrorText
                  errorText={formik.errors.passwordConfirmation}
                />
              )}
            </InputWrapper>
          </>
        ) : (
          <InputWrapper width='w-full'>
            <Label text={t('password')} />
            <p className='text-lightGray'>***********************</p>
          </InputWrapper>
        )}
      </div>
    </form>
  )
}

export default Security
